'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import Image from 'next/image';
import Link from 'next/link';

import { PostSummary } from '../actions';

import { TruncateText } from './TruncateText';

type Props = { onNavigate: (slug: string) => void; post: PostSummary };

const domain = process.env.NODE_ENV === 'development' ? '' : (process.env.NEXT_PUBLIC_WWW_URL ?? '');

export const PostPreview = ({ onNavigate, post }: Props) => {
  const slug = post.slug ?? '';
  const image = post.featuredImage?.node;
  const category = post.categories?.nodes?.[0];

  const url = `${domain}/home-solar/blog/${category?.slug ?? 'other'}/${slug}`;
  const imageSrc = image?.sourceUrl ? new URL(image.sourceUrl).pathname : '';

  return (
    <DS.Card borderWidth={0} data-testid={post.slug} flexGrow={1} p={0}>
      <x.a
        as={Link}
        display="block"
        h={280}
        href={url}
        onClick={() => onNavigate(slug)}
        overflow="hidden"
        position="relative"
        w="100%"
      >
        {image ? (
          <Image
            alt={image.altText ?? 'Post Image'}
            fill
            sizes="(min-width: 1280px) 405px, (min-width: 768px) 45vw, 92vw"
            src={imageSrc}
            style={{ objectFit: 'cover' }}
          />
        ) : (
          <x.span bg="uiBgAlternate" display="block" h="100%" w="100%" />
        )}
      </x.a>

      <DS.Flex flexDirection="column" flexGrow={1} gap={4} px={{ _: 0, md: 6 }}>
        {category ? (
          <DS.Text display={{ _: 'none', md: 'block' }} fontWeight={500} text={{ _: 'body14', lg: 'body16' }}>
            {category.name}
          </DS.Text>
        ) : null}

        <Link href={url} onClick={() => onNavigate(slug)}>
          <DS.Text as="h3" fontSize={{ _: 'h2', lg: 'h1' }} fontWeight={200} lineHeight={1.2}>
            {post.title}
          </DS.Text>
        </Link>
        {post.blogFields?.summary ? (
          <TruncateText textProps={{ text: { _: 'body16' } }}>{post.blogFields?.summary}</TruncateText>
        ) : null}
        <DS.Flex flexDirection="column" flexGrow={1} justifyContent="end">
          <x.a
            alignItems="center"
            as={Link}
            display="flex"
            flexDirection="row"
            gap={2}
            href={url}
            onClick={() => onNavigate(slug)}
            w="100%"
          >
            <DS.Button as="span" icon={DS.IconArrowRight} size="md" />
            <x.span color="uiHelperCopy">Read more</x.span>
          </x.a>
        </DS.Flex>
      </DS.Flex>
    </DS.Card>
  );
};

export const PostPreviewSkeleton = () => {
  return (
    <DS.Card borderWidth={0} flexGrow={1} p={0}>
      <DS.SkeletonLoader h={280} mb={0} w="100%" />
      <DS.Flex flexDirection="column" flexGrow={1} px={{ _: 0, md: 6 }}>
        <DS.SkeletonLoader display={{ _: 'none', md: 'block' }} h={5} mb={0} w="40%" />
        <DS.SkeletonLoader h={88} mb={0} w="100%" />
        <DS.SkeletonLoader h={80} mb={4} w="100%" />
        <DS.SkeletonLoader h={8} mb={0} w={8} />
      </DS.Flex>
    </DS.Card>
  );
};
